import { usesHostedBilling } from '@/lib/appDistribution';
import React, { useCallback, useMemo, useState } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useAuthStore, useModalStore } from '@/store';
import { useGabPurchase } from '@/context/GabPurchaseContext';
import { useBillingPalette } from '@/components/Settings/billing/BillingGroup';
import useResponsiveness from '@/hooks/useResponsiveness';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import CreditStorePacks from './CreditStorePacks';
import CreditStoreTopUpCard from './CreditStoreTopUpCard';
import CreditsFAQs from './CreditsFAQs';
import { formatCreditBalance } from '@/lib/formatCredits';
import { formatCreditResetSchedule } from '@/lib/creditResetDate';
import { PLUS_MONTHLY_CREDITS_DISPLAY, PLUS_PREMIUM_MONTHLY_CREDITS_DISPLAY, PRICING_PLANS } from '@/constants/config';

function CreditsPurchaseModalControl({
  isPlus, isPlusPremium, isIapSubscriber, onClose, credits,
  monthlyCredits, purchasedCredits, totalAvailableCredits, initialTab, promoCode,
}) {
  const palette = useBillingPalette();
  const { isXS } = useResponsiveness();
  const styles = useMemo(() => createStyles(palette, isXS), [palette, isXS]);
  const { openModal, closeModal } = useModalStore(state => state.actions);
  const user = useAuthStore(state => state.user);
  const { usesWhopCheckout } = useGabPurchase();
  const router = useRouter();
  const [showBalance, setShowBalance] = useState(initialTab === 'my-credits');
  const [showQuestions, setShowQuestions] = useState(false);
  const directBilling = usesHostedBilling();
  const showUpgrade = !isPlus || (!isPlusPremium && !isIapSubscriber && directBilling);
  const showTopUp = directBilling && !!user && (!usesWhopCheckout || !!user.autoTopup?.whopAvailable);
  const balance = totalAvailableCredits ?? credits?.totalAvailable ?? 0;
  const resetSchedule = formatCreditResetSchedule(monthlyCredits?.resetDate);

  const handleUpgrade = useCallback(() => {
    onClose?.();
    closeModal();
    if (Platform.OS === 'ios') {
      setTimeout(() => router.push('/(auth)/paywall'), 100);
    } else {
      setTimeout(() => openModal('PricingModal'), 100);
    }
  }, [onClose, closeModal, openModal, router]);

  const topUpCard = <CreditStoreTopUpCard onClose={onClose} onUpgrade={handleUpgrade} compact={isXS} />;

  return (
    <View style={styles.container}>
      <View style={styles.heading}>
        <View style={styles.headingCopy}>
          <Text tag="h1" size="xxxl" weight="bold" style={styles.title}>Add credits</Text>
          <Text size="n" color="secondary">A little more room for your next idea.</Text>
        </View>
        <Pressable accessibilityRole="button" accessibilityLabel="Close credit store" onPress={onClose}
          style={({ hovered, pressed }) => [styles.close, (hovered || pressed) && styles.hover]}>
          <Icon name="xmark" size={16} color={palette.ink} />
        </Pressable>
      </View>

      <View style={styles.balanceSection}>
        <Pressable accessibilityRole="button" accessibilityLabel="View credit balance"
          accessibilityState={{ expanded: showBalance }} onPress={() => setShowBalance(value => !value)}
          {...(Platform.OS === 'web' ? { 'aria-expanded': showBalance } : {})}
          style={({ hovered, pressed }) => [styles.balance, (hovered || pressed) && styles.hover]}>
          <View style={styles.balanceLabel}>
            <Icon name="coins" size={15} color={palette.colors.text_color_secondary} />
            <Text size="s" color="secondary">Available credits</Text>
          </View>
          <View style={styles.balanceLabel}>
            <Text size="l" weight="bold">{formatCreditBalance(balance)}</Text>
            <Icon name={showBalance ? 'chevron-up' : 'chevron-down'} size={10} color={palette.colors.text_color_secondary} />
          </View>
        </Pressable>
        {showBalance && <View style={styles.balanceDetails}>
          {isPlus && monthlyCredits && <>
            <View style={styles.detailRow}><Text size="s" color="secondary">Monthly credits remaining</Text><Text size="s">{formatCreditBalance(monthlyCredits.remaining ?? 0)}</Text></View>
            {!!resetSchedule && <Text size="xs" color="secondary">Resets {resetSchedule}</Text>}
          </>}
          <View style={styles.detailRow}><Text size="s" color="secondary">Purchased credits</Text><Text size="s">{formatCreditBalance(purchasedCredits?.available ?? 0)}</Text></View>
          <Text size="xs" color="secondary">Purchased credits never expire.</Text>
        </View>}
      </View>

      {isXS && showTopUp && topUpCard}
      <View style={styles.columns}>
        <View style={styles.packs}><CreditStorePacks promoCode={promoCode} /></View>
        {(showUpgrade || (!isXS && showTopUp)) && <View style={styles.sidebar}>
          {!isXS && showTopUp && topUpCard}
          {showUpgrade && <View style={styles.upgrade}>
            <View style={styles.balanceLabel}>
              <Icon name={isPlus ? 'gem' : 'sparkles'} size={16} color={palette.ink} />
              <Text size="n" weight="bold">{isPlus ? 'More with Plus Premium' : 'More every month with Plus'}</Text>
            </View>
            <Text size="s" color="secondary" style={styles.bodyCopy}>
              {isPlus ? `${PLUS_PREMIUM_MONTHLY_CREDITS_DISPLAY} monthly credits, with more room for your work.` : `Free default-model messages and ${PLUS_MONTHLY_CREDITS_DISPLAY} monthly credits.`}
            </Text>
            {directBilling && <Text size="xs" color="secondary">From ${isPlus ? PRICING_PLANS.plusPremium.annualMonthlyPrice : PRICING_PLANS.plus.annualMonthlyPrice}/mo, billed annually</Text>}
            <Pressable accessibilityRole="button" onPress={handleUpgrade}
              style={({ hovered, pressed }) => [styles.upgradeButton, (hovered || pressed) && styles.hover]}>
              <Text size="s" weight="bold">{isPlus ? 'Explore Plus Premium' : 'Explore Plus'}</Text>
              <Icon name="arrow-right" size={12} color={palette.ink} />
            </Pressable>
          </View>}
        </View>}
      </View>

      <View style={styles.questions}>
        <Pressable accessibilityRole="button" accessibilityState={{ expanded: showQuestions }}
          {...(Platform.OS === 'web' ? { 'aria-expanded': showQuestions } : {})}
          onPress={() => setShowQuestions(value => !value)}
          style={({ hovered, pressed }) => [styles.questionsToggle, (hovered || pressed) && styles.hover]}>
          <Text size="s" weight="medium">Questions about credits?</Text>
          <Icon name={showQuestions ? 'chevron-up' : 'chevron-down'} size={11} color={palette.colors.text_color_secondary} />
        </Pressable>
        {showQuestions && <CreditsFAQs />}
      </View>
    </View>
  );
}

const createStyles = (palette, isXS) => StyleSheet.create({
  container: { padding: isXS ? 20 : 32, gap: 24, width: '100%', flexShrink: 0 },
  heading: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 },
  headingCopy: { flex: 1, minWidth: 0, gap: 6 },
  title: { letterSpacing: -0.8 },
  close: { width: 44, height: 44, borderRadius: 22, backgroundColor: palette.groupFill, justifyContent: 'center', alignItems: 'center' },
  hover: { backgroundColor: palette.hover },
  balanceSection: { borderRadius: 14, backgroundColor: palette.groupFill, overflow: 'hidden' },
  balance: { minHeight: 52, paddingHorizontal: 16, paddingVertical: 12, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 12 },
  balanceLabel: { flexDirection: 'row', alignItems: 'center', gap: 9, flexShrink: 1 },
  balanceDetails: { gap: 8, padding: 16, paddingTop: 0 },
  detailRow: { flexDirection: 'row', justifyContent: 'space-between', gap: 12 },
  columns: { flexDirection: isXS ? 'column' : 'row', gap: isXS ? 24 : 28, alignItems: 'flex-start' },
  packs: { flex: isXS ? undefined : 1, minWidth: 0, width: isXS ? '100%' : undefined },
  sidebar: { width: isXS ? '100%' : 280, gap: 20, flexShrink: 0 },
  upgrade: { padding: 4, gap: 8 },
  bodyCopy: { lineHeight: 20 },
  upgradeButton: { minHeight: 44, flexDirection: 'row', alignItems: 'center', gap: 8, alignSelf: 'flex-start', paddingHorizontal: 8, marginLeft: -8, borderRadius: 8 },
  questions: { borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: palette.separator, paddingTop: 8, gap: 12 },
  questionsToggle: { minHeight: 44, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', borderRadius: 8, paddingHorizontal: 4 },
});

export default React.memo(CreditsPurchaseModalControl);
